import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import config from '../config';
import DataTable from '../shared/DataTable';
import { useAuth } from '../context/AuthContext';
import { selectStyles, selectTheme } from '../theme/selectTheme';

const API_URL = config.apiUrl;

const ROLE_OPTIONS = ['admin', 'executive'];

const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ username: '', password: '', name: '', role: 'executive', blocks: [] });
  const [submitting, setSubmitting] = useState(false);
  const [blockEdits, setBlockEdits] = useState({});
  const [savingBlock, setSavingBlock] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/users`);
      if (res.data?.success) setUsers(res.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlocks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/filters`);
      if (res.data?.success) setBlocks(res.data.blocks || []);
    } catch (err) {
      console.error('Error fetching blocks:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchBlocks();
  }, [fetchUsers, fetchBlocks]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const payload = { ...form, blocks: form.blocks.map((o) => o.value) };
      const res = await axios.post(`${API_URL}/users`, payload);
      if (res.data?.success) {
        setMessage(`User "${form.username}" added.`);
        setForm({ username: '', password: '', name: '', role: 'executive', blocks: [] });
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (username) => {
    if (!window.confirm(`Remove user "${username}"?`)) return;
    setError('');
    setMessage('');
    try {
      const res = await axios.delete(`${API_URL}/users/${encodeURIComponent(username)}`);
      if (res.data?.success) {
        setMessage(`User "${username}" removed.`);
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user.');
    }
  };

  const handleBlockSave = async (username) => {
    setSavingBlock(username);
    setError('');
    setMessage('');
    try {
      const selected = blockEdits[username] || [];
      const res = await axios.patch(`${API_URL}/users/${encodeURIComponent(username)}`, {
        blocks: selected.map((o) => o.value),
      });
      if (res.data?.success) {
        setMessage(`Updated block access for "${username}".`);
        setBlockEdits((e) => { const n = { ...e }; delete n[username]; return n; });
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update block access.');
    } finally {
      setSavingBlock('');
    }
  };

  const blockOptions = toOptions(blocks);

  const columns = [
    { key: 'username', label: 'Username', render: (r) => <span className="cell-strong">{r.username}</span> },
    { key: 'name', label: 'Name', render: (r) => r.name || '—' },
    {
      key: 'role',
      label: 'Role',
      render: (r) => (
        <span className={`badge ${r.role === 'admin' ? 'badge-info' : 'badge-neutral'}`}>{r.role}</span>
      ),
    },
    {
      key: 'block',
      label: 'Block Access',
      render: (r) => {
        const savedOptions = toOptions(r.blocks);
        const current = blockEdits[r.username] ?? savedOptions;
        const savedValues = (r.blocks || []).slice().sort().join(',');
        const currentValues = current.map((o) => o.value).slice().sort().join(',');
        const dirty = currentValues !== savedValues;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 220 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <Select
                isMulti
                options={blockOptions}
                value={current}
                onChange={(opts) => setBlockEdits((edits) => ({ ...edits, [r.username]: opts || [] }))}
                placeholder="All Blocks"
                styles={selectStyles}
                theme={selectTheme}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
            {dirty && (
              <button
                className="btn btn-primary"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                onClick={() => handleBlockSave(r.username)}
                disabled={savingBlock === r.username}
                title="Save block access"
              >
                <SaveIcon fontSize="inherit" />
              </button>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      render: (r) => (
        <button
          className="btn btn-danger"
          style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}
          onClick={() => handleRemove(r.username)}
          disabled={r.username === user?.username}
          title={r.username === user?.username ? 'You cannot remove yourself' : 'Remove user'}
        >
          <DeleteIcon fontSize="small" /> Remove
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <PeopleIcon /> Users
        </h1>
        <p className="page-subtitle">Add or remove dashboard users, and control which block(s) each user can see data for.</p>
      </div>

      <div className="card">
        <h3 className="card-title">Add User</h3>
        <form onSubmit={handleAdd}>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Username</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
            <div className="filter-group">
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div className="filter-group">
              <label>Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required
                style={inputStyle}
              />
            </div>
            <div className="filter-group">
              <label>Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="page-size-select"
                style={{ height: 38 }}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Block Access (blank = all blocks)</label>
              <Select
                isMulti
                options={blockOptions}
                value={form.blocks}
                onChange={(opts) => setForm((f) => ({ ...f, blocks: opts || [] }))}
                placeholder="All Blocks"
                styles={selectStyles}
                theme={selectTheme}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              <PersonAddIcon fontSize="small" /> {submitting ? 'Adding…' : 'Add User'}
            </button>
          </div>
        </form>
        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="card">
        <h3 className="card-title">Existing Users</h3>
        <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '-0.4rem' }}>
          No blocks selected = sees everything. Assigning one or more blocks restricts that user to only that data everywhere in the app — filters, dashboards, and drilldowns.
        </p>
        <DataTable
          columns={columns}
          rows={users}
          loading={loading}
          emptyMessage="No users found."
          getRowKey={(r) => r.username}
        />
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  fontSize: '0.9rem',
  height: 38,
  fontFamily: 'inherit',
};
